import { AuthService } from '@lved/shared-core';
import { platformApi } from '@lved/api-gateway';
import { AUTH_LOGGED_IN_CONTEXT_KEY } from '@theia-lved-core/browser/shell/auth-context-contribution';
import { CommandContribution, CommandRegistry, MenuContribution, MenuModelRegistry } from '@theia/core';
import { AccountMenu } from '@theia-lved-core/browser/shell/menu-contribution';
import { MANAGE_MENU } from '@theia/core/lib/common/menu/menu-types';
import { injectable } from '@theia/core/shared/inversify';
import type { FileStoreState, LicenseStoreState, UIStoreState } from '@lved/ui';

export const LvglLicenseCommands = {
    OPEN_MANAGER: { id: 'lvgl.license.openManager', label: 'Manage License' },
    LOGOUT: { id: 'lvgl.license.logout', label: 'Sign out' }
} as const;

const LICENSE_OVERLAY_NODE_ID: string = 'lvgl-license-overlay';
const LICENSE_OVERLAY_HANDOFF_BLOCKER_CLASS: string = 'lvgl-license-overlay-handoff-blocker';
const LICENSE_OVERLAY_HANDOFF_BLOCKER_MS: number = 900;

let licenseOverlayHandoffBlockerTimeout: number | undefined;

/**
 * Keeps the license host painted during the Project Launcher -> License Selector handoff,
 * so the workspace cannot flash while both overlays update their store-backed visibility.
 */
const showLicenseOverlayHandoffBlocker = (): void => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const overlayNode: HTMLElement | null = document.getElementById(LICENSE_OVERLAY_NODE_ID);
    if (!overlayNode) return;

    overlayNode.classList.add(LICENSE_OVERLAY_HANDOFF_BLOCKER_CLASS);

    if (licenseOverlayHandoffBlockerTimeout !== undefined) {
        window.clearTimeout(licenseOverlayHandoffBlockerTimeout);
    }

    licenseOverlayHandoffBlockerTimeout = window.setTimeout((): void => {
        overlayNode.classList.remove(LICENSE_OVERLAY_HANDOFF_BLOCKER_CLASS);
        licenseOverlayHandoffBlockerTimeout = undefined;
    }, LICENSE_OVERLAY_HANDOFF_BLOCKER_MS);
};

const executeOpenLicenseManager = async (): Promise<void> => {
    const { useLicenseStore, useUIStore } = await import('@lved/ui');
    const licenseStore: LicenseStoreState = useLicenseStore.getState();
    const uiStore: UIStoreState = useUIStore.getState();

    if (uiStore.launchScreenOpen) {
        // Close the launcher only after the license host is already blocking the editor.
        showLicenseOverlayHandoffBlocker();
        licenseStore.setShowLicenseSelector(true);
        uiStore.setProjectSelectionRequired(false);
        uiStore.setLaunchScreenOpen(false);
        return;
    }

    licenseStore.setShowLicenseSelector(true);
};

/**
 * Clear local state immediately, then reset the workspace after server-side sign-out finishes.
 */
const executeLogoutToLicenseManager = async (): Promise<void> => {
    const { useAuthStore, useLicenseStore, useFileStore } = await import('@lved/ui');
    const fileStore: FileStoreState = useFileStore.getState();

    if (!(await fileStore.confirmUnsavedFiles())) return;

    const openedProject: string | null = await platformApi.getOpenedProject();
    if (openedProject !== null) {
        await platformApi.saveOpenedProject(null);

        if ((await platformApi.getOpenedProject()) !== null) return;
    }

    const releaseSeatPromise: Promise<void> = useLicenseStore.getState().releaseSeat('logout_user_profile');
    const clearLicensePromise: Promise<void> = useLicenseStore.getState().clear();
    const authLogoutPromise: Promise<void> = useAuthStore
        .getState()
        .logout()
        .catch((error: unknown) => {
            console.error('Error clearing local auth state:', error);
        });

    useLicenseStore.getState().setShowLicenseSelector(true);

    try {
        try {
            await releaseSeatPromise;
        } catch (error: unknown) {
            console.error('Error releasing seat:', error);
        }

        try {
            await new AuthService().logout();
        } catch (error: unknown) {
            console.error('Error logging out from Appwrite:', error);
        }
    } finally {
        await Promise.all([clearLicensePromise, authLogoutPromise]);
        fileStore.clearWorkspace();
    }
};

@injectable()
export class LicenseMenuContribution implements CommandContribution, MenuContribution {
    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand(LvglLicenseCommands.OPEN_MANAGER, {
            execute: () => executeOpenLicenseManager()
        });
        commands.registerCommand(LvglLicenseCommands.LOGOUT, {
            execute: () => executeLogoutToLicenseManager()
        });
    }

    registerMenus(menus: MenuModelRegistry): void {
        menus.registerMenuAction(AccountMenu, {
            commandId: LvglLicenseCommands.OPEN_MANAGER.id,
            label: LvglLicenseCommands.OPEN_MANAGER.label,
            order: '0',
            when: `${AUTH_LOGGED_IN_CONTEXT_KEY} && workspaceFolderCount > 0`
        });
        menus.registerMenuAction(AccountMenu, {
            commandId: LvglLicenseCommands.LOGOUT.id,
            label: LvglLicenseCommands.LOGOUT.label,
            order: '1',
            when: AUTH_LOGGED_IN_CONTEXT_KEY
        });
        menus.registerMenuAction(MANAGE_MENU, {
            commandId: LvglLicenseCommands.OPEN_MANAGER.id,
            label: 'Sign in or manage licenses…',
            order: '5',
            when: `!${AUTH_LOGGED_IN_CONTEXT_KEY} && workspaceFolderCount > 0`
        });
        menus.registerMenuAction(MANAGE_MENU, {
            commandId: LvglLicenseCommands.OPEN_MANAGER.id,
            label: LvglLicenseCommands.OPEN_MANAGER.label,
            order: '5',
            when: `${AUTH_LOGGED_IN_CONTEXT_KEY} && workspaceFolderCount > 0`
        });
        menus.registerMenuAction(MANAGE_MENU, {
            commandId: LvglLicenseCommands.LOGOUT.id,
            label: LvglLicenseCommands.LOGOUT.label,
            order: '6',
            when: AUTH_LOGGED_IN_CONTEXT_KEY
        });
    }
}
